// Binding wording — changes require Product role-agent comment.
//
// User-visible copy strings for the activity-review surface (D5),
// drafted as part of feature-200 pre-development edits per Product
// gap-test findings #22–#25. Drafted as string constants here so the
// implementer reaches a single source-of-truth file when wiring the
// React components in D5; no React component edits live in this file
// yet (commented placeholders only — wiring lands in the
// implementation PR's D5 commit).
//
// Voice rules these strings follow:
//   - First-person and user-voiced (this is the user's own time,
//     their own work record, their own day). The assistant speaks
//     AS the professional, not AT them.
//   - No implementation jargon: no "ambient", no "AI pipeline", no
//     "deterministic classification", no "rule-based classification".
//     Replace with what the user actually sees: "known patterns",
//     "AI proposals", "your usual rules", "the events themselves".
//   - No surveillance voice: never "we detected" / "we observed" /
//     "the system has identified". Instead: "Kanjoy noticed",
//     "Kanjoy proposed", "Kanjoy recognizes".
//   - Reversible-friendly: no "are you sure? this cannot be undone"
//     theatrics. The 5-min toast and the 24h drill-down are the
//     undo surfaces; the user should never feel cornered.
//
// See feature-200-task.md §D5 for the surfaces these strings render
// on, and §D7 item 16 for the operations.md source-of-truth pointer
// that mirrors privacy-statement.tsx.

// ---------------------------------------------------------------------
// 1. Activity-review tab name on EmployeeReviewPage (gap #25)
// ---------------------------------------------------------------------
// Renders as the tab label next to the existing "Needs Your Review"
// tab. The user's own word. Plural because the tab shows many.
export const ACTIVITY_REVIEW_TAB_LABEL = 'Activities';

// ---------------------------------------------------------------------
// 2. Privacy-drawer trigger visible label (gap #25)
// ---------------------------------------------------------------------
// Renders next to the shield icon at the top-right of the activity
// tab's content region. "Privacy statement" reads as a legal artifact;
// "How your data is used" reads as user-facing — pick the latter.
// The aria-label remains 'Open privacy statement' (no copy collision
// in screen readers).
export const PRIVACY_DRAWER_TRIGGER_LABEL = 'How your data is used';

// ---------------------------------------------------------------------
// 3. Confirm picker hint copy (gap #24)
// ---------------------------------------------------------------------
// Renders as one-line hints inside the picker, below each option.
// Pins the floating-vs-Personal-vs-Project semantic distinction in
// user voice (matches the table in feature-200-task.md § Resolved R2
// and ADR 0044 § Decision 2).
export const CONFIRM_PICKER_HINT_PERSONAL =
  'Your own time. Won’t show up in client billing or exports. You can change this later.';
export const CONFIRM_PICKER_HINT_LEAVE_UNMATCHED =
  'Keep this activity grouped without picking a project. Kanjoy may propose a better project on later passes.';

// ---------------------------------------------------------------------
// 4. Kill-switch / pause-toggle hint copy — rewritten from the jargony
//    drafts at task.md §D5 (gap #22)
// ---------------------------------------------------------------------
// Org-level toggle on SettingsOrgPage. Replaces the prior draft
// "ambient and signal-based proposals continue / AI-based proposals
// are paused" which the Product reviewer flagged as implementation
// jargon. The user-facing distinction is "we keep recognizing your
// work from known patterns; what pauses is the part where we ask an
// AI to summarize a session and propose a new activity."
export const ORG_KILL_SWITCH_HINT =
  'When off, Kanjoy keeps recognizing work from known patterns — what pauses is the part where it asks an AI to summarize and propose new activities. Pauses for everyone in the workspace. Switch back on anytime.';

// User-level pause toggle on UserSettingsPage. Replaces the prior
// draft "the AI pipeline skips this employee's events / Deterministic
// classification continues." which read as system-on-user.
export const USER_PAUSE_HINT =
  'When on, Kanjoy won’t analyze your activity with AI. Your events still get tied to clients and projects by your usual rules. Toggle off anytime to resume.';

// ---------------------------------------------------------------------
// 5. Kill-switch ON→OFF confirmation modal (gap #23)
// ---------------------------------------------------------------------
// Triggered when an admin flips the org-level toggle from on to off.
// Three goals per Product: (a) confirm the admin understands the
// org-wide scope, (b) state what continues working, (c) be
// reversible-friendly. No "are you sure" theatrics — this is undone
// instantly by toggling back on.
export const KILL_SWITCH_MODAL_TITLE = 'Pause AI proposals for the whole workspace?';
export const KILL_SWITCH_MODAL_BODY =
  'Kanjoy will keep recognizing work from known patterns. What pauses is the part where it asks an AI to summarize sessions and propose new activities — for everyone in the workspace. The activities already proposed stay where they are; anyone can still confirm, edit, or dismiss them. You can switch this back on anytime from Workspace settings.';
export const KILL_SWITCH_MODAL_CONFIRM_BUTTON = 'Pause AI proposals';
export const KILL_SWITCH_MODAL_CANCEL_BUTTON = 'Keep them on';

// ---------------------------------------------------------------------
// 6. 5-min inline Undo toast (gap #23)
// ---------------------------------------------------------------------
// Renders on confirm. The countdown is derived from undoExpiresAt
// returned by POST /activities/:id/confirm. After 5 min the toast
// disappears; the 24h stored-undo affordance lives in the activity
// drill-down (item 7 below). The toast does not assert the eventual
// disappearance ("This will disappear in 5 minutes") — it just
// shows the countdown.
//
// Format strings carry one interpolation slot each ({name} for the
// activity name; {countdown} for the live MM:SS countdown).
export const UNDO_TOAST_MESSAGE_TEMPLATE = '“{name}” confirmed.';
export const UNDO_TOAST_ACTION_LABEL = 'Undo';
export const UNDO_TOAST_COUNTDOWN_TEMPLATE = 'Undo ({countdown})';

// ---------------------------------------------------------------------
// 7. 24h stored-Undo drill-down affordance (gap #23)
// ---------------------------------------------------------------------
// Renders inside the activity drill-down panel for activities whose
// activity_confirm_undo row is unexpired. Use the absolute clock
// time, not "expires in Xh", per Product — "available until 14:22
// tomorrow" reads more clearly than countdown math.
//
// Format strings:
//   {datetime} — locale-formatted absolute clock time of expiry
//     (e.g. "Tuesday, May 14 at 2:22 PM").
export const STORED_UNDO_AFFORDANCE_TEMPLATE = 'Undo confirm — available until {datetime}.';
export const STORED_UNDO_AFFORDANCE_ACTION = 'Undo confirm';

// ---------------------------------------------------------------------
// 8. Post-undo-window (>24h) dismiss confirmation modal (gap #23)
// ---------------------------------------------------------------------
// Renders when a user tries to dismiss a confirmed activity more
// than 24 hours after confirm (the X-Confirm-Dismiss header path).
// The prior draft mentioned "affected events return to their
// rule-based classification" — implementation jargon. User voice:
// "the events will show up as individual events again, attributed
// by your usual rules." Also reassures that manual classifications
// survive.
//
// Format strings:
//   {name} — activity name
//   {count} — number of events that were attached to the activity
export const POST_WINDOW_DISMISS_MODAL_TITLE_TEMPLATE = 'Dismiss “{name}”?';
export const POST_WINDOW_DISMISS_MODAL_BODY_TEMPLATE =
  'The {count} events you had attached to this activity will go back to how they were before — they’ll show up as individual events, attributed to clients and projects by your usual rules. Anything you manually attached to a project stays where you put it.';
export const POST_WINDOW_DISMISS_MODAL_CONFIRM_BUTTON = 'Dismiss activity';
export const POST_WINDOW_DISMISS_MODAL_CANCEL_BUTTON = 'Keep it';

// ---------------------------------------------------------------------
// 9. "Previously dismissed — re-propose?" affordance (gap #23)
// ---------------------------------------------------------------------
// Renders as an inline strip above the proposed-activities list
// when classify-persist suppressed a proposal because its signals
// hash matched a previously-dismissed activity (see feature-200-
// task.md §D4 step 4 "result === 'new'" branch). Product flagged
// that "we've seen this pattern more strongly" reads as creepy —
// the safer user-voice framing is "you've seen this kind of thing
// before; want to take another look?", with no claim about why
// the system is re-surfacing it.
//
// Format strings:
//   {label} — the dismissed activity's name
export const PREVIOUSLY_DISMISSED_STRIP_TEMPLATE =
  'You dismissed “{label}” before. Want to take another look at it?';
export const PREVIOUSLY_DISMISSED_STRIP_ACTION = 'Take another look';
export const PREVIOUSLY_DISMISSED_STRIP_SECONDARY = 'Keep it dismissed';

// ---------------------------------------------------------------------
// 10. 410 Gone "undo window expired" / 409 Conflict "confirmation
//     required" user-visible messages (gap #23)
// ---------------------------------------------------------------------
// When the frontend gets 410 from POST /undo-confirm it shows this
// inline. When it gets 409 from POST /dismiss (post-undo-window
// path without the confirmation header) it pops the modal at #8
// above. The 410 case is read by the user as "the window passed,
// here's the path forward" — not "you did something wrong."
export const UNDO_WINDOW_EXPIRED_MESSAGE =
  'The 24-hour undo window has passed. You can still dismiss this activity — that will un-attach the events.';
export const UNDO_WINDOW_EXPIRED_ACTION = 'Dismiss activity';

// ---------------------------------------------------------------------
// 11. Org-level AI toggle label on SettingsOrgPage (gap #22 follow-up)
// ---------------------------------------------------------------------
// Renders as the Toggle label in the "Privacy & AI controls" section.
// The prior implementation used the jargon label "Activity AI pipeline"
// which the Product reviewer flagged. User voice: name what the toggle
// controls (proposals), not what the system calls it (pipeline).
export const ORG_KILL_SWITCH_TOGGLE_LABEL = 'AI activity proposals';

// Disabled-state hint shown when the admin tries to scan while the
// workspace kill-switch is off. Frames the state as a feature being
// paused, not an error.
export const SCAN_DISABLED_KILL_SWITCH_HINT =
  'AI activity proposals are paused for this workspace.';

// ---------------------------------------------------------------------
// 12. Scan-range modal copy (EmployeeReviewPage)
// ---------------------------------------------------------------------
// Renders as the success toast after the admin submits a scan request,
// and as the checkbox label for the "also re-examine ambient events"
// option.
export const SCAN_RANGE_QUEUED_MESSAGE =
  'Scan queued — Kanjoy will process the selected date range.';

// Checkbox that tells Kanjoy to re-examine events it previously set
// aside as ambient (no notable work context). User voice: name the
// user's intent, not the pipeline's vocabulary.
export const SCAN_RANGE_REPROCESS_NOISE_LABEL = 'Also review events Kanjoy previously set aside';

// ---------------------------------------------------------------------
// Notes for implementers:
//
// - This file is the single source of truth for these strings. Every
//   React component that renders the activity surfaces above imports
//   from here, not from string literals at the call site (drift-
//   resistance, mirrors the privacy-statement.tsx import pattern).
//
// - Template strings carry literal "{name}", "{count}", "{datetime}",
//   "{countdown}", "{label}" placeholders. The components do simple
//   .replace() at render time (no i18n framework in v1 — pre-empt
//   one in the same shape if introduced later).
//
// - No copy edits without a Product role-agent comment on the PR.
//   The voice rules at the top of this file are binding; the strings
//   are not legal text but they are the user's first read of how
//   Kanjoy talks to them.
